import React, { useState, useEffect } from 'react';
import './Preparados.css';
import Header from '../common/Header';
import Footer from '../common/Footer';
import FormModal from '../common/FormModal';
import FormField from '../common/FormField';
import FormGrid from '../common/FormGrid';
import SearchBar from '../common/SearchBar';
import { useToast } from '../../contexts/ToastContext';
import { FaSave, FaEdit, FaPlus, FaFlask, FaTrashAlt, FaBalanceScale } from 'react-icons/fa';
import PreparadoIngredientesModal from './PreparadoIngredientesModal';
import { getApiUrl } from '../../utils/api';

const Preparados = () => {
  const [preparados, setPreparados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [preparadoSeleccionado, setPreparadoSeleccionado] = useState(null);
  const [formData, setFormData] = useState({ nombre: '', unidad: '' });
  const [unidades, setUnidades] = useState([]);

  // Modal ingredientes
  const [showIngredientesModal, setShowIngredientesModal] = useState(false);
  const [preparadoIngredientes, setPreparadoIngredientes] = useState(null);

  const [filtroNombre, setFiltroNombre] = useState('');
  const toast = useToast();

  useEffect(() => {
    cargarDatos();
    cargarUnidades();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'getTodosLosPreparados' })
      });

      const data = await response.json();

      if (data.success) {
        setPreparados(data.data || []);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar preparados');
    } finally {
      setLoading(false);
    }
  };

  const cargarUnidades = async () => {
    try {
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'getUnidades' })
      });
      const data = await response.json();
      if (data.success) {
        setUnidades(data.data || []);
      }
    } catch (error) {
      console.error('Error al cargar unidades:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const nuevoPreparado = () => {
    setFormData({ nombre: '', unidad: '' });
    setPreparadoSeleccionado(null);
    setShowModal(true);
  };

  const editarPreparado = (preparado) => {
    setFormData({ nombre: preparado.nombre || '', unidad: preparado.unidad || '' });
    setPreparadoSeleccionado(preparado);
    setShowModal(true);
  };

  const guardarPreparado = async () => {
    try {
      setGuardando(true);

      if (!formData.nombre.trim()) {
        toast.error('El nombre del preparado es obligatorio');
        return;
      }

      const esEdicion = !!preparadoSeleccionado;
      const payload = {
        tipo: esEdicion ? 'actualizarPreparado' : 'crearPreparado',
        nombre: formData.nombre,
        unidad: formData.unidad
      };

      if (esEdicion) {
        payload.idPreparado = preparadoSeleccionado.idPreparado;
      }

      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        if (esEdicion) {
          setPreparados(prev => prev.map(p =>
            p.idPreparado === preparadoSeleccionado.idPreparado
              ? { ...p, nombre: formData.nombre, unidad: formData.unidad }
              : p
          ));
        } else {
          const nuevoPreparadoData = {
            idPreparado: data.data?.idPreparado,
            nombre: formData.nombre,
            unidad: formData.unidad,
            numIngredientes: 0
          };
          setPreparados(prev => [...prev, nuevoPreparadoData]);
        }

        toast.success(esEdicion ? 'Preparado actualizado correctamente' : 'Preparado creado correctamente');
        handleCloseModal();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error al guardar preparado:', error);
      toast.error('Error al guardar preparado');
    } finally {
      setGuardando(false);
    }
  };

  const eliminarPreparado = async (preparado) => {
    if (!window.confirm(`¿Está seguro de eliminar el preparado "${preparado.nombre}"?`)) {
      return;
    }

    try {
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'eliminarPreparado', idPreparado: preparado.idPreparado })
      });

      const data = await response.json();

      if (data.success) {
        setPreparados(prev => prev.filter(p => p.idPreparado !== preparado.idPreparado));
        toast.success('Preparado eliminado correctamente');
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error al eliminar preparado:', error);
      toast.error('Error al eliminar preparado');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setPreparadoSeleccionado(null);
    setFormData({ nombre: '', unidad: '' });
  };

  // Filtrar preparados
  const preparadosFiltrados = preparados.filter(p =>
    p.nombre && p.nombre.toLowerCase().includes(filtroNombre.toLowerCase())
  );

  if (loading) {
    return (
      <div className="preparados-container">
        <div className="preparados-loading">Cargando preparados...</div>
      </div>
    );
  }

  return (
    <div className="preparados-container">
      <Header titulo='Preparados' color='#4CAF50' />
      <div className="preparados-content">
        <div className="preparados-inner">
          <div className="preparados-header">
            <h1 className="preparados-titulo">Gestión de Preparados</h1>
            <div className="preparados-botones-header">
              <button onClick={nuevoPreparado} className="btn-nuevo-preparado">
                <FaPlus /> Nuevo Preparado
              </button>
            </div>
          </div>

          <div className="preparados-panel-filtros">
            <div className="preparados-filtros-content">
              <div className="preparados-filtros-row">
                <div className="preparados-filtro-item">
                  <label>Búsqueda de Preparados</label>
                </div>
                <div className="preparados-filtro-derecha">
                  <SearchBar
                    value={filtroNombre}
                    onChange={(e) => setFiltroNombre(e.target.value)}
                    placeholder="Buscar por nombre..."
                    size="medium"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="preparados-panel-listado">
            <div className="preparados-tabla-container">
              {preparadosFiltrados.length === 0 ? (
                <div className="preparados-sin-datos">No se encontraron preparados.</div>
              ) : (
                <table className="tabla-preparados">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Unidad</th>
                      <th>Núm. Ingredientes</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preparadosFiltrados.map(preparado => (
                      <tr key={preparado.idPreparado}>
                        <td className="preparados-nombre">{preparado.nombre}</td>
                        <td>{preparado.unidad || '-'}</td>
                        <td className="preparados-num-ingredientes">
                          <span className="preparados-badge-ingredientes">{preparado.numIngredientes}</span>
                        </td>
                        <td>
                          <div className="preparados-acciones">
                            <button
                              onClick={() => editarPreparado(preparado)}
                              className="preparados-btn-accion preparados-btn-editar"
                              title="Editar preparado"
                            >
                              <FaEdit />
                            </button>
                            <button
                              onClick={() => { setPreparadoIngredientes(preparado); setShowIngredientesModal(true); }}
                              className="preparados-btn-accion preparados-btn-ingredientes"
                              title="Ingredientes"
                            >
                              <FaFlask />
                            </button>
                            <button
                              onClick={() => eliminarPreparado(preparado)}
                              className="preparados-btn-accion preparados-btn-eliminar"
                              title="Eliminar preparado"
                            >
                              <FaTrashAlt />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="preparados-acciones-container">
              <div className="preparados-info">
                <span>Total de preparados: {preparadosFiltrados.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />

      {/* Modal crear/editar preparado */}
      <FormModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={preparadoSeleccionado ? 'Editar Preparado' : 'Nuevo Preparado'}
        onSave={guardarPreparado}
        saveLabel={preparadoSeleccionado ? 'Actualizar' : 'Crear'}
        isSaving={guardando}
        size="small"
        saveIcon={preparadoSeleccionado ? <FaSave /> : <FaPlus />}
      >
        <div className="preparados-modal-section">
          <div className="preparados-modal-section-title">
            <FaFlask /> Datos del Preparado
          </div>
          <FormGrid columns={2} gap="12px">
            <FormField
              label="Nombre"
              name="nombre"
              value={formData.nombre}
              onChange={handleInputChange}
              placeholder="Nombre del preparado"
              required
              icon={<FaFlask />}
            />
            <FormField
              label="Unidad"
              type="select"
              name="unidad"
              value={formData.unidad}
              onChange={handleInputChange}
              icon={<FaBalanceScale />}
              options={[
                { value: '', label: 'Seleccione unidad' },
                ...unidades.map(u => ({ value: u.prefijo, label: `${u.nombre} (${u.prefijo})` }))
              ]}
            />
          </FormGrid>
        </div>
      </FormModal>

      {/* Modal de ingredientes */}
      <PreparadoIngredientesModal
        isOpen={showIngredientesModal}
        onClose={() => { setShowIngredientesModal(false); setPreparadoIngredientes(null); cargarDatos(); }}
        preparado={preparadoIngredientes}
      />
    </div>
  );
};

export default Preparados;
